import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  hasPermission, hasSomePermission, manageSurveyPermission, openEndedQuestionsPermission, surveyOverviewPermission
} from 'unihow-common';
import useTitle from '../../hooks/useTitle';
import {
  addErrorMessageAction, updateSurveyOptionsAction
} from '../../actions';
import { getTranslation, langType, withLang } from '../../i18n';
import StatisticsInfoBox from './StatisticsInfoBox';
import styles from './surveyStatistics.module.css';
import WithStatistics from '../WithStatistics';

import {
  getSurveyReminders
} from '../../api';
import { surveyTypeValues } from '../../constants/types';
import ActionButtonGroup from './ActionButtonGroup';
import Summary from './Summary';
import {
  isSurveyForDegreeProgram
} from '../../util';
import useCurrentUser from '../../hooks/useCurrentUser';
import OpenEndedQuestions from './OpenEndedQuestions';

const SurveyStatistics = ({
  surveyType,
  surveyStats,
  surveyInfo,
  surveySchedule,
  lang,
  translate: t
}) => {
  const dispatch = useDispatch();
  const [surveyReminders, setSurveyReminders] = useState([]);
  const params = useParams();
  const { permissions: userPermissions } = useCurrentUser();

  const fetchingRemindersAllowed = isSurveyForDegreeProgram(surveyType)
    && hasPermission(userPermissions, manageSurveyPermission(surveyType));

  useTitle(surveyInfo && surveySchedule ? getTranslation(surveyInfo.title, lang) : '');

  useEffect(() => {
    if (surveyInfo) {
      dispatch(updateSurveyOptionsAction({ surveyType: surveyInfo.type, ...surveyInfo }));
    }
  }, [surveyInfo]);

  useEffect(() => {
    if (fetchingRemindersAllowed) {
      getSurveyReminders(params.surveyId)
        .then((surveyRemindersResponse) => {
          setSurveyReminders(surveyRemindersResponse);
        }).catch((error) => dispatch(addErrorMessageAction(error)));
    }
  }, []);

  return (surveyInfo && surveySchedule
    ? (
      <div className="page-container">
        <div className={styles.titleRow}>
          <h1 className={`page-title--small ${styles.title}`}>
            {getTranslation(surveyInfo.title, lang)}
          </h1>
        </div>
        <ActionButtonGroup
          surveyId={params.surveyId}
          surveySchedule={surveySchedule}
          surveyInfo={surveyInfo}
        />
        <StatisticsInfoBox
          surveyInfo={surveyInfo}
          surveySchedule={surveySchedule}
          lang={lang}
          surveyReminders={surveyReminders}
          translate={t}
        />
        {hasPermission(userPermissions, surveyOverviewPermission(surveyType))
          && (
            <Summary
              params={params}
              surveyStats={surveyStats}
              surveyInfo={surveyInfo}
              surveyType={surveyType}
              surveySchedule={surveySchedule}
            />
          )}
        {hasSomePermission(userPermissions, [
          manageSurveyPermission(surveyType),
          openEndedQuestionsPermission(surveyType)
        ])
          && (
            <OpenEndedQuestions surveyInfo={surveyInfo} surveyStats={surveyStats} surveyType={surveyType} />
          )}

      </div>
    ) : null
  );
};

const {
  shape,
  oneOf
} = PropTypes;

SurveyStatistics.propTypes = {
  surveyStats: shape(),
  surveySchedule: shape(),
  surveyType: oneOf(Object.values(surveyTypeValues)).isRequired,
  ...langType
};

SurveyStatistics.defaultProps = {
  surveyStats: null,
  surveySchedule: null
};

// Gets router props (params, navigate) from WithStatistics.
export default withLang(WithStatistics(SurveyStatistics));
